// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ShareThis from "@mcmc/App/Components/ShareThis/ShareThis";
import loadAllLocations from "@mcmc/App/Handlers/AllLocations/LoadAllLocations";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getAllLocations, getAllLocationsLoadingState } from "@mcmc/App/Store/Selectors/AllLocationsSelectors";
import { totalSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import LazyImage, { LazyImageProps } from "@mcmc/MobiusOverrides/LazyImage";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import Notify from "@mcmc/XDL/Components/Notify";
import { screen } from "@mcmc/XDL/Helpers";
import { printIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => ({
    allLocations: getAllLocations(state),
    isLoading: getAllLocationsLoadingState(state),
    locationDetailsPageLink: getPageLinkByPageType(state, "LocationFinderPage")?.url,
});

const mapDispatchToProps = {
    loadAllLocations,
    setBreadcrumbActions,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AllLocationsStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    mainLocationsWrapper?: InjectableCss;
    mainLocationsContent?: InjectableCss;
    companyLogo?: LazyImageProps;
    mainLocationsTitle?: TypographyProps;
    locationsWrapper?: InjectableCss;
    locationsContent?: InjectableCss;
    addressInfoWrapper?: InjectableCss;
    locationNameLink?: LinkPresentationProps;
    locationPhoneNumberLink?: LinkPresentationProps;
}

export const allLocationsStyles: AllLocationsStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    mainLocationsWrapper: {
        css: css`
            margin-bottom: 30px;
            @media print {
                page-break-after: always;
            }
        `,
    },
    mainLocationsContent: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 20px;
            margin: -10px;
        `,
    },
    companyLogo: {
        css: css`
            max-height: 75px;
            max-width: 215px;
            width: 100%;
            padding: 10px;
        `,
    },
    mainLocationsTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 0;
            padding: 10px;
        `,
    },
    locationsWrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            grid-gap: 20px;
            ${screen("lte", "sm")`
                grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
           `}
            @media print {
                width: auto;
                display: block;
                &:after {
                    content: "";
                    clear: both;
                    display: table;
                }
            }
        `,
    },
    locationsContent: {
        css: css`
            @media print {
                page-break-inside: avoid;
                width: 50%;
                display: block;
                float: left;
                &:after {
                    content: "";
                    clear: both;
                    display: table;
                }
            }
        `,
    },
    addressInfoWrapper: {
        css: css`
            padding: 20px;
            background-color: ${getColor("common.background")};
            box-shadow: ${getProp("theme.shadows.1")};
            height: 100%;

            @media print {
                box-shadow: none;
                background-color: unset;
            }
        `,
    },
    locationNameLink: {
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            margin-bottom: 10px;
        `,
    },
};

const styles = allLocationsStyles;

const AllLocations: React.FC<Props> = ({
    loadAllLocations,
    setBreadcrumbActions,
    allLocations,
    isLoading,
    locationDetailsPageLink,
}) => {
    React.useEffect(() => {
        if (!allLocations) {
            loadAllLocations({});
        }
    }, []);

    React.useEffect(() => {
        setBreadcrumbActions({
            breadcrumbActions: (
                <StyledWrapper {...totalSectionGlobalStyles.breadcrumbActionsWrapper}>
                    <ShareThis />
                    <Link
                        {...printIconStyle}
                        onClick={openPrintDialog}
                        aria-label={translate("Print All Locations Page")}
                    >
                        <VisuallyHidden>{translate("Print")}</VisuallyHidden>
                    </Link>
                </StyledWrapper>
            ),
        });
        return () => {
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, []);

    if (isLoading || !allLocations) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (
        !isLoading &&
        allLocations &&
        allLocations.locationsCollection &&
        allLocations.locationsCollection.length === 0
    ) {
        return <Notify messageType="danger" hideCloseButton body={translate("No Data Found.")} />;
    }

    return (
        <>
            {allLocations.locationsCollection.map((mainLocations, index) => {
                return (
                    <StyledBox
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        {...styles.mainLocationsWrapper}
                    >
                        <StyledWrapper {...styles.mainLocationsContent}>
                            <Typography {...styles.mainLocationsTitle}>{mainLocations.companyName}</Typography>
                            {mainLocations.companyLogo && (
                                <LazyImage
                                    src={mainLocations.companyLogo}
                                    altText={mainLocations.companyName}
                                    {...styles.companyLogo}
                                />
                            )}
                        </StyledWrapper>
                        {mainLocations.locations?.length > 0 && (
                            <StyledWrapper {...styles.locationsWrapper}>
                                {mainLocations.locations.map(location => {
                                    return (
                                        <StyledWrapper key={location.id} {...styles.locationsContent}>
                                            <StyledWrapper {...styles.addressInfoWrapper}>
                                                <Link
                                                    href={`${locationDetailsPageLink}/DealerDetails?id=${location?.properties?.urlSegment}`}
                                                    {...styles.locationNameLink}
                                                >
                                                    {location.name}
                                                </Link>
                                                <AddressInfoDisplay
                                                    address1={location.streetAddress}
                                                    state={location.state}
                                                    postalCode={location.zipCode}
                                                    city={location.city}
                                                    phone={location.phoneNumber}
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        )}
                    </StyledBox>
                );
            })}
        </>
    );
};

const allLocationsWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AllLocations),
    definition: {
        group: "Location Finder",
        allowedContexts: ["AllLocationsPage" as any],
    },
};

export default allLocationsWidget;
